import { useState, useEffect, useRef } from "react";
import { X, UserPlus, Eye, EyeOff, Check, ArrowRight, ArrowLeft, Mail, Phone, Shield, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDraggable } from "../../hooks/useDraggable";
import { useEscClose } from "../../hooks/useEscClose";
import { usePopupTopOffset } from "../../hooks/usePopupTopOffset";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useCountries, useCities } from "../../hooks/useBusinessData";
import { getRecaptchaToken } from "../../hooks/useRecaptcha";
import CustomSelect from "./CustomSelect";
import { useConfirm } from "./ConfirmDialog";

import TermsViewPopup from "./TermsViewPopup";

interface SignupPopupProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
}

type Step = 1 | 2;

export default function SignupPopup({ onClose, onSwitchToLogin }: SignupPopupProps) {
  const { t } = useTranslation();
  const dbCountries = useCountries();
  const [closing, setClosing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { position, onMouseDown, dragged, prepareClose } = useDraggable();
  const popupTop = usePopupTopOffset("bottom");  // title-bar(로고바) 하단 = 검색바 top 에 맞춤

  // Step
  const [step, setStep] = useState<Step>(1);
  const [slideDir, setSlideDir] = useState<"left" | "right">("left");

  // Step 1: Account
  const [uid, setUid] = useState("");
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isAdvertiser, setIsAdvertiser] = useState(false);

  // 약관 동의 — 단일 토글 (TOS + PRIVACY 통합 표기, 백엔드 consents 배열은 두 항목 동일 값 전송)
  const [agreeTerms, setAgreeTerms] = useState(false);
  // 약관 본문 view — 모달 안에서 직접 표시 (URL 변경 없음 → 백버튼이 메인으로 점프 안 함)
  const [viewingTerms, setViewingTerms] = useState(false);

  // Step 2: Location & Contact
  const [country, setCountry] = useState("kr");
  const [city, setCity] = useState("");
  const dbCities = useCities(country);
  // 도시 기본값 — 국가에 맞춰 자동 매칭: 한국=서울, 필리핀=마닐라. 못 찾으면 첫 도시
  useEffect(() => {
    if (city || dbCities.length === 0) return;
    let target = null as typeof dbCities[number] | null;
    if (country === "kr") {
      target = dbCities.find((c) =>
        (c.key || "").toLowerCase() === "seoul" ||
        (c.label || "").toLowerCase().includes("seoul") ||
        (c.label || "").includes("서울")
      ) || null;
    } else if (country === "ph") {
      target = dbCities.find((c) =>
        (c.key || "").toLowerCase() === "manila" ||
        (c.label || "").toLowerCase().includes("manila") ||
        (c.label || "").includes("마닐라")
      ) || null;
    }
    setCity(String((target || dbCities[0]).id));
  }, [dbCities, city, country]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Step 3: Verification
  const [verifyMethod, setVerifyMethod] = useState<"EMAIL" | "PHONE" | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [codeVerifying, setCodeVerifying] = useState(false);
  const [codeMessage, setCodeMessage] = useState("");

  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const selectedCountry = dbCountries.find((c) => c.key === country);

  // Which contact methods are available
  const hasEmail = email.trim() !== "";
  const hasPhone = phone.trim() !== "";
  // 유효성 — 인증코드 받기 버튼 활성 조건
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const isPhoneValid = /^\d{7,15}$/.test(phone.trim());
  // 첫 render 부터 즉시 카드 노출. 항상 EMAIL/PHONE 중 하나 결정 (null 없음).
  // 둘 다 입력 또는 전화번호만 입력 → PHONE. 이메일만 입력 또는 둘 다 미입력 → EMAIL.
  const effectiveVerifyMethod: "EMAIL" | "PHONE" =
    verifyMethod || (hasPhone ? "PHONE" : "EMAIL");

  const { confirm, dialog: confirmDialog } = useConfirm();

  // city 는 마운트 시 마닐라로 자동 세팅되므로 isDirty 판정에서 제외 — 사용자 명시 변경 아님
  const isDirty = !submitted && (
    uid.trim() !== "" ||
    nickname.trim() !== "" ||
    password !== "" ||
    phone.trim() !== "" ||
    email.trim() !== "" ||
    isAdvertiser ||
    verifyCode.trim() !== ""
  );

  const handleClose = async () => {
    if (isDirty && !(await confirm(t("signup.cancelConfirm") || "변경사항이 있습니다. 회원가입 진행을 중단하시겠습니까?"))) return;
    prepareClose();
    setClosing(true);
    setTimeout(onClose, 700);
  };

  useEscClose(handleClose);
  const containerRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(containerRef);

  // Step validation — 필드별 에러 힌트
  const validateStep1 = (): boolean => {
    setError("");
    const fe: Record<string, string> = {};
    if (!uid.trim() || uid.length < 4 || !/^[a-zA-Z0-9_-]+$/.test(uid)) fe.uid = t("signup.validUid");
    if (!nickname.trim()) fe.nickname = t("signup.validNickname");
    if (!password.trim()) fe.password = t("signup.validPassword");
    else if (password.length < 8) fe.password = t("signup.validPasswordLength");
    if (password !== passwordConfirm) fe.passwordConfirm = t("signup.passwordMismatch") || "비밀번호가 일치하지 않습니다.";
    setFieldError(fe);
    return Object.keys(fe).length === 0;
  };

  const validateStep2 = (): boolean => {
    setError("");
    const fe: Record<string, string> = {};
    // 국가/도시 제거(#374) — 도시 필수 검증 삭제
    if (!hasEmail && !hasPhone) fe.contact = t("signup.validEmailOrPhone");
    setFieldError(fe);
    return Object.keys(fe).length === 0;
  };

  const goNext = async () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;

    // Step 1 → 2: UID 중복 즉시 확인
    if (step === 1) {
      try {
        const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";
        const res = await fetch(`${API_URL}/user/auth/check-uid?uid=${encodeURIComponent(uid)}`);
        const data = await res.json();
        if (data.status !== "success") {
          const msg = t("msg." + data.code) || data.code || t("signup.uidUnavailable") || "이미 사용 중인 아이디입니다.";
          setFieldError((p) => ({ ...p, uid: msg }));
          window.alert(msg);
          return;
        }
      } catch {
        setError(t("msg.NETWORK_ERROR"));
        return;
      }
    }

    // Step 2 (통합) 가 마지막 step — 추가 navigation 분기 없음. verify method/check-contact 는 send code 시점에 처리.

    setError("");
    setFieldError({});
    setSlideDir("left");
    setStep((s) => Math.min(s + 1, 2) as Step);
  };

  const goBack = () => {
    setError("");
    setFieldError({});
    setSlideDir("right");
    setStep((s) => Math.max(s - 1, 1) as Step);
  };

  // Send verification code
  const handleSendCode = async (method?: "EMAIL" | "PHONE") => {
    // Step 1/2 통합 후 verifyMethod 미확정인 경우 자동 결정 (hasEmail/hasPhone 기반)
    const m = method || verifyMethod || (hasEmail ? "EMAIL" : hasPhone ? "PHONE" : null);
    if (!m) { setError(t("signup.validEmailOrPhone")); return; }
    if (m === "EMAIL" && !hasEmail) { setError(t("signup.validEmail")); return; }
    if (m === "PHONE" && !hasPhone) { setError(t("signup.validPhone")); return; }

    // If choosing method, set it
    if (method || !verifyMethod) setVerifyMethod(m);

    // 이메일/전화 중복 확인 — send code 전에 차단
    setCodeSending(true);
    setCodeMessage("");
    setError("");
    try {
      const API_URL2 = process.env.REACT_APP_API_URL || "https://api.trippang.com";
      const params = new URLSearchParams();
      if (m === "EMAIL") params.set("email", email);
      else params.set("phone", `${selectedCountry?.phone_code || ""}${phone}`);
      const checkRes = await fetch(`${API_URL2}/user/auth/check-contact?${params}`);
      const checkData = await checkRes.json();
      if (checkData.status !== "success") {
        const msg = t("msg." + checkData.code) || checkData.code;
        const field = checkData.code === "DUPLICATE_EMAIL" ? "email" : checkData.code === "DUPLICATE_PHONE" ? "contact" : null;
        if (field) setFieldError((p) => ({ ...p, [field]: msg }));
        window.alert(msg);
        setCodeSending(false);
        return;
      }
    } catch {
      setError(t("msg.NETWORK_ERROR"));
      setCodeSending(false);
      return;
    }
    try {
      let recaptchaToken = "";
      try { recaptchaToken = await getRecaptchaToken("send_code"); } catch {}
      const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";
      const identifier = m === "EMAIL" ? email : `${selectedCountry?.phone_code || ""}${phone}`;
      const res = await fetch(`${API_URL}/user/auth/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, method: m, recaptcha_token: recaptchaToken }),
      });
      const data = await res.json();
      if (data.status === "success") {
        if (data.data?.test_mode && data.data?.test_code) {
          // 테스트 모드 (도메인 미인증 시): 코드를 화면에 표시
          setCodeMessage(`[TEST] ${data.data.test_code}`);
        } else {
          setCodeMessage(t("signup.codeSent"));
        }
      } else {
        setError(t("msg." + data.code) || data.code);
      }
    } catch { setError(t("msg.NETWORK_ERROR")); }
    finally { setCodeSending(false); }
  };

  // Verify code
  const handleVerifyCode = async () => {
    if (!verifyCode.trim() || !verifyMethod) return;
    setCodeVerifying(true);
    setError("");
    try {
      const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";
      const identifier = verifyMethod === "EMAIL" ? email : `${selectedCountry?.phone_code || ""}${phone}`;
      const res = await fetch(`${API_URL}/user/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, code: verifyCode }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setVerified(true);
        setCodeMessage("");
        setError("");
      } else {
        setError(t("msg." + data.code) || data.code);
      }
    } catch { setError(t("msg.NETWORK_ERROR")); }
    finally { setCodeVerifying(false); }
  };

  // Submit
  const handleSubmit = async () => {
    if (!agreeTerms) {
      setError(t("signup.requireConsent") || "약관 및 개인정보 처리방침에 동의해주세요.");
      return;
    }
    if (!verified) {
      if (verifyCode.length !== 6) { setError(t("signup.codePlaceholder") || "인증코드를 입력해주세요."); return; }
      await handleVerifyCode();
      return;
    }
    setError("");
    setLoading(true);
    try {
      let recaptchaToken = "";
      try { recaptchaToken = await getRecaptchaToken("signup"); } catch {}

      const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";
      const res = await fetch(`${API_URL}/user/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid,
          upw: password,
          nick_name: nickname || null,
          email: email || null,
          phone: phone ? `${selectedCountry?.phone_code || ""}${phone}` : null,
          city_id: parseInt(city) || 1,
          language_id: null,
          is_advertiser_pending: isAdvertiser,
          recaptcha_token: recaptchaToken,
          consents: [
            { consent_type: "TOS", consent_version: "1.0", is_agreed: agreeTerms },
            { consent_type: "PRIVACY", consent_version: "1.0", is_agreed: agreeTerms },
          ],
        }),
      });
      const data = await res.json();

      if (data.status !== "success") {
        if (data.detail && Array.isArray(data.detail)) {
          setError(data.detail[0]?.msg || t("msg.NETWORK_ERROR"));
        } else {
          setError(t("msg." + data.code) || data.code || t("msg.NETWORK_ERROR"));
        }
        return;
      }

      setSubmitted(true);
      setTimeout(() => {
        handleClose();
        setTimeout(onSwitchToLogin, 800);
      }, 2500);
    } catch {
      setError(t("msg.NETWORK_ERROR"));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-1 text-sm text-slate-900 placeholder-slate-400 outline-none transition-all focus:border-amber-400/50 focus:bg-slate-100 focus:ring-1 focus:ring-amber-400/30";

  const labelClass = "mb-1.5 block text-xs font-semibold text-sky-600";

  const slideAnim = slideDir === "left"
    ? "stepSlideInRight 0.35s ease-out forwards"
    : "stepSlideInLeft 0.35s ease-out forwards";

  return (
    <div ref={containerRef} className="fixed left-0 right-0 bottom-0 z-[120] flex items-start justify-center overflow-y-auto" style={{ top: popupTop }} onClick={handleClose}>
      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{
          animation: closing
            ? "flipOverlayIn 0.7s ease reverse forwards"
            : "flipOverlayIn 0.5s ease forwards",
        }}
      />

      {/* Popup */}
      <div
        className="relative z-10 w-full max-w-none overflow-hidden border-0 border-t-[0.5px] border-t-blue-500 bg-white shadow-2xl shadow-black/50 md:mx-4 md:max-w-md md:rounded-3xl md:border md:border-slate-200 md:border-t-[0.5px] md:border-t-blue-500"
        style={{
          animation: dragged ? "none" : (closing
            ? "zoomOut 0.7s ease-in-out forwards"
            : "zoomIn 1.1s cubic-bezier(0.22, 1, 0.36, 1) forwards"),
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          /* Confetti + Success */
          <div className="relative flex flex-col items-center justify-center overflow-hidden py-16 text-center">
            {Array.from({ length: 50 }, (_, i) => {
              const colors = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7", "#ec4899", "#facc15"];
              const color = colors[i % colors.length];
              const left = Math.random() * 100;
              const delay = Math.random() * 0.8;
              const duration = 1.5 + Math.random() * 1;
              const size = 4 + Math.random() * 8;
              const shape = i % 3 === 0 ? "50%" : i % 3 === 1 ? "0%" : "30%";
              return (
                <div
                  key={i}
                  className="absolute top-0"
                  style={{
                    left: `${left}%`, width: size, height: size, borderRadius: shape,
                    background: color, animation: `confettiFall ${duration}s ${delay}s ease-out forwards`,
                  }}
                />
              );
            })}
            <div style={{ animation: "confettiBurst 0.5s ease-out forwards" }}>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
                <Check className="h-8 w-8 text-green-400" />
              </div>
              <div className="text-2xl font-black text-slate-900">{t("signup.welcome")}</div>
              <div className="mt-2 text-sm text-slate-600">{t("signup.complete")}</div>
              <div className="mt-1 text-xs text-amber-400">{t("signup.benefit")}</div>
            </div>
          </div>
        ) : (
          <div className="py-6 px-[9px] md:px-[13px]">
            {/* Header — 회원가입 라벨 | (중앙) 스텝 인디케이터 | X. 구분선 0.5px 블루 + 전폭(본문 좌우 패딩 무시) */}
            <div className="-mx-[9px] px-[9px] md:-mx-[13px] md:px-[13px] pb-3 mb-4 border-b-[0.5px] border-blue-500 flex items-center gap-2">
              <div className="flex items-center gap-2.5 shrink-0" onMouseDown={onMouseDown} style={{ cursor: "move" }}>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-300">
                  <UserPlus className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">{t("signup.title")}</h2>
              </div>
              {/* Step Indicator — 라벨과 X 사이 중앙 */}
              <div className="flex-1 flex items-center justify-center gap-1.5">
                {[1, 2].map((s) => (
                  <div key={s} className="flex items-center gap-1.5">
                    <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all ${
                      step >= s
                        ? "bg-gradient-to-br from-amber-400 to-orange-300 text-white shadow-lg shadow-amber-500/25"
                        : "border border-slate-300 text-slate-500"
                    }`}>
                      {step > s ? <Check className="h-3 w-3" /> : s}
                    </div>
                    {s < 2 && (
                      <div className={`h-0.5 w-4 rounded-full transition-all ${step > s ? "bg-blue-500" : "bg-blue-200"}`} />
                    )}
                  </div>
                ))}
              </div>
              <button onClick={handleClose} className="shrink-0 rounded-full p-1.5 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300">
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
            </div>

            {/* Error — API 에러 등 필드에 매핑할 수 없는 에러만 표시 */}
            {error && !Object.keys(fieldError).length && (
              <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-400">
                {error}
              </div>
            )}

            {/* Step Content */}
            <div className="min-h-[280px]" key={step} style={{ animation: slideAnim }}>

              {/* ── Step 1: Account ── */}
              {step === 1 && (
                <div className="space-y-4">
                  {/* 광고주 신청 체크박스 — 모든 가입은 일반회원, 체크 시 광고주 신청 대기 상태로 등록 */}
                  <div>
                    <label
                      onClick={() => setIsAdvertiser(!isAdvertiser)}
                      className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 hover:bg-slate-100 transition-colors"
                    >
                      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${isAdvertiser ? "border-amber-400 bg-amber-400" : "border-slate-300 bg-white"}`}>
                        {isAdvertiser && <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{t("signup.advertiserApply")}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 whitespace-pre-line">{t("signup.advertiserApplyHint")}</div>
                      </div>
                    </label>
                  </div>

                  {/* UID */}
                  <div>
                    <label className={labelClass}>{t("signup.uid")} <span className="text-amber-400">*</span></label>
                    <input
                      type="text" autoComplete="off"
                      value={uid}
                      onChange={(e) => { setUid(e.target.value.toLowerCase()); setFieldError((p) => { const { uid: _, ...r } = p; return r; }); }}
                      placeholder={t("signup.uidPlaceholder")}
                      className={`${inputClass} ${fieldError.uid ? "!border-red-400/50" : ""}`}
                      autoFocus
                    />
                    {fieldError.uid && <p className="mt-1 text-[11px] text-red-400">{fieldError.uid}</p>}
                  </div>

                  {/* Nickname */}
                  <div>
                    <label className={labelClass}>{t("signup.nickname")} <span className="text-amber-400">*</span></label>
                    <input
                      type="text" autoComplete="off"
                      value={nickname}
                      onChange={(e) => { setNickname(e.target.value.replace(/\s/g, "")); setFieldError((p) => { const { nickname: _, ...r } = p; return r; }); }}
                      placeholder={t("signup.nicknamePlaceholder")}
                      className={`${inputClass} ${fieldError.nickname ? "!border-red-400/50" : ""}`}
                    />
                    {fieldError.nickname && <p className="mt-1 text-[11px] text-red-400">{fieldError.nickname}</p>}
                  </div>

                  {/* Password */}
                  <div>
                    <label className={labelClass}>{t("signup.password")} <span className="text-amber-400">*</span></label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setFieldError((p) => { const { password: _, ...r } = p; return r; }); }}
                        placeholder={t("signup.passwordHint")}
                        className={`${inputClass} pr-10 ${fieldError.password ? "!border-red-400/50" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-slate-700"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {fieldError.password && <p className="mt-1 text-[11px] text-red-400">{fieldError.password}</p>}
                  </div>

                  {/* Password Confirm */}
                  <div>
                    <label className={labelClass}>{t("signup.passwordConfirm") || "비밀번호 확인"} <span className="text-amber-400">*</span></label>
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={passwordConfirm}
                      onChange={(e) => { setPasswordConfirm(e.target.value); setFieldError((p) => { const { passwordConfirm: _, ...r } = p; return r; }); }}
                      className={`${inputClass} ${fieldError.passwordConfirm ? "!border-red-400/50" : ""}`}
                    />
                    {fieldError.passwordConfirm && <p className="mt-1 text-[11px] text-red-400">{fieldError.passwordConfirm}</p>}
                  </div>
                </div>
              )}

              {/* ── Step 2: Location & Contact ── */}
              {step === 2 && (
                <div className="space-y-4">
                  {/* 안내 문구 (#374) — 전화/이메일 중 하나 필수 */}
                  <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] text-sky-700 whitespace-pre-line">
                    {t("signup.contactNotice") || "전화번호 또는 이메일 중 하나를 입력해야\n회원가입이 가능합니다."}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className={labelClass}>{t("signup.phone")}</label>
                    <div className="flex gap-2">
                      <div className="flex w-[72px] shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700">
                        {selectedCountry?.phone_code ?? ""}
                      </div>
                      <input
                        type="text" inputMode="tel" autoComplete="off" name={`p_${Math.random().toString(36).slice(2)}`}
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value.replace(/[^0-9]/g, "")); setFieldError((p) => { const { contact: _, ...r } = p; return r; }); }}
                        placeholder=""
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Email + 인라인 인증 라인 (이메일 입력란 바로 아래 상주) */}
                  <div>
                    <label className={labelClass}>
                      {t("signup.email")}
                      {!hasPhone && <span className="text-amber-400"> *</span>}
                    </label>
                    <input
                      type="text" inputMode="email" autoComplete="off" name={`e_${Math.random().toString(36).slice(2)}`}
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setFieldError((p) => { const { contact: _, ...r } = p; return r; }); }}
                      placeholder=""
                      className={inputClass}
                    />
                    {fieldError.contact && <p className="mt-1 text-[11px] text-red-400">{fieldError.contact}</p>}

                    {/* 인증 라인 — 한 줄 상주. 입력란 좌측, 버튼 우측. 코드 받기 전엔 입력란 비활성 */}
                    {!verified ? (
                      <div className="mt-2 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500 flex items-center gap-1 shrink-0">
                            {effectiveVerifyMethod === "PHONE"
                              ? <><Phone className="h-3.5 w-3.5 text-green-500" /> {t("signup.smsVerifyCode") || "문자 인증코드"}</>
                              : <><Mail className="h-3.5 w-3.5 text-sky-500" /> {t("signup.emailVerifyCode") || "이메일 인증코드"}</>}
                          </span>
                          <input
                            type="text" autoComplete="off"
                            value={verifyCode}
                            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder={t("signup.codePlaceholder")}
                            maxLength={6}
                            disabled={effectiveVerifyMethod === "EMAIL" ? !isEmailValid : !isPhoneValid}
                            className={`${inputClass} !w-[78px] !px-1.5 !text-xs text-center font-mono tracking-wider ${(effectiveVerifyMethod === "EMAIL" ? !isEmailValid : !isPhoneValid) ? "bg-slate-100 cursor-not-allowed" : ""}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleSendCode()}
                            disabled={codeSending || (effectiveVerifyMethod === "EMAIL" ? !isEmailValid : !isPhoneValid)}
                            className={`shrink-0 inline-flex items-center gap-1 rounded-lg px-1.5 py-2 text-xs font-bold text-white transition-all disabled:opacity-40 ${effectiveVerifyMethod === "PHONE" ? "bg-green-500 hover:bg-green-400" : "bg-sky-500 hover:bg-sky-400"}`}
                          >
                            {codeSending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            {codeSending ? t("signup.loading") : codeMessage ? t("signup.resendCode") : t("signup.sendCode")}
                          </button>
                          <button
                            type="button"
                            onClick={handleVerifyCode}
                            disabled={codeVerifying || !codeMessage || verifyCode.length !== 6}
                            className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-amber-500 px-1.5 py-2 text-xs font-bold text-white hover:bg-amber-400 transition-all disabled:opacity-40"
                          >
                            {codeVerifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("signup.verifyCode")}
                          </button>
                        </div>
                        {codeMessage && (
                          <div className="text-[11px] text-sky-600">{codeMessage}</div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-green-600">
                        <Shield className="h-3.5 w-3.5" />
                        {t("signup.verified")}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-1.5 py-3">
                    <span className="flex-1 min-w-0 text-xs text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis">
                      <span className="text-rose-500 mr-0.5">*</span>
                      {t("signup.consentToTerms") || "약관 및 개인정보 처리방침 동의"}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setViewingTerms(true); }}
                      className="text-xs font-semibold text-sky-600 hover:underline shrink-0"
                    >
                      {t("signup.viewTerms") || "약관보기"}
                    </button>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={agreeTerms}
                      onClick={() => setAgreeTerms((v) => !v)}
                      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${agreeTerms ? "bg-green-500" : "bg-slate-300"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${agreeTerms ? "translate-x-5" : "translate-x-0"}`} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="mt-6 flex items-center gap-3">
              {step > 1 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}

              {step < 2 ? (
                <button
                  type="button"
                  onClick={goNext}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#bdb76b] py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-[#9ea05a]/30 transition-all hover:bg-[#a8a25c] active:scale-[0.98]"
                >
                  {t("signup.next")}
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !verified}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:shadow-amber-500/40 hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  {loading ? t("signup.loading") : t("signup.submit")}
                </button>
              )}
            </div>

            {/* Login Link */}
            <div className="mt-5 text-center text-xs text-slate-500">
              {t("signup.hasAccount")}{" "}
              <button
                onClick={() => { handleClose(); setTimeout(onSwitchToLogin, 800); }}
                className="font-semibold text-yellow-500 transition-colors hover:text-yellow-600 hover:underline"
              >
                {t("signup.login")}
              </button>
            </div>
          </div>
        )}
      </div>
      {viewingTerms && <TermsViewPopup onClose={() => setViewingTerms(false)} />}
      {confirmDialog}
    </div>
  );
}
